import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const WorkspaceIntroPage: React.FC = () => {
  const { language } = useLanguage();
  const t = language === 'zh-TW' ? zhTW : enUS;

  const workspaceTypes = [
    {
      icon: '🏢',
      title: t.types.business.title,
      description: t.types.business.description,
      features: t.types.business.features,
      price: t.types.business.price,
      bestFor: t.types.business.bestFor
    },
    {
      icon: '🌊',
      title: t.types.seaview.title,
      description: t.types.seaview.description,
      features: t.types.seaview.features,
      price: t.types.seaview.price,
      bestFor: t.types.seaview.bestFor
    },
    {
      icon: '🏔️',
      title: t.types.mountain.title,
      description: t.types.mountain.description,
      features: t.types.mountain.features,
      price: t.types.mountain.price,
      bestFor: t.types.mountain.bestFor
    },
    {
      icon: '☕',
      title: t.types.cafe.title,
      description: t.types.cafe.description,
      features: t.types.cafe.features,
      price: t.types.cafe.price,
      bestFor: t.types.cafe.bestFor
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* 頁面標題 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {t.subtitle}
          </p>
          <Link
            to="/start"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-medium"
          >
            💼 {t.cta}
          </Link>
        </div>

        {/* 工作空間特色 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
          <div className="text-center">
            <div className="text-4xl mb-4">📶</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.features.wifi.title}</h3>
            <p className="text-gray-600">{t.features.wifi.description}</p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">🪑</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.features.comfort.title}</h3>
            <p className="text-gray-600">{t.features.comfort.description}</p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">🤝</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.features.community.title}</h3>
            <p className="text-gray-600">{t.features.community.description}</p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">💰</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.features.flexible.title}</h3>
            <p className="text-gray-600">{t.features.flexible.description}</p>
          </div>
        </div>

        {/* 工作空間類型 */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">
            {t.types.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {workspaceTypes.map((type, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-soft p-8 hover:shadow-medium transition-all duration-300 transform hover:scale-105"
              >
                <div className="text-5xl mb-4">{type.icon}</div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-3">
                  {type.title}
                </h3>
                <p className="text-gray-600 mb-6">{type.description}</p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-2">💰</span>
                    {type.price}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-2">👥</span>
                    {type.bestFor}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {type.features.map((feature, featureIndex) => (
                    <span
                      key={featureIndex}
                      className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 設施特色 */}
        <div className="bg-white rounded-2xl shadow-soft p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-8">
            {t.facilities.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {t.facilities.list.map((facility, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-4">{facility.icon}</div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {facility.name}
                </h3>
                <p className="text-gray-600 text-sm">
                  {facility.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 預約流程 */}
        <div className="bg-white rounded-2xl shadow-soft p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-8">
            {t.process.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {t.process.steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 行動按鈕 */}
        <div className="text-center">
          <Link
            to="/start"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-medium"
          >
            🎯 {t.startBooking}
          </Link>
        </div>
      </div>
    </div>
  );
};

// 中文翻譯
const zhTW = {
  title: '共用工作空間',
  subtitle: '從安靜的圖書館到熱鬧的創業空間，從海景工作室到山景工作坊，我們提供多樣化的工作環境選擇',
  cta: '開始預約工作空間',
  startBooking: '立即開始預約',
  features: {
    wifi: {
      title: '高速網路',
      description: '穩定快速的WiFi，確保工作效率'
    },
    comfort: {
      title: '舒適環境',
      description: '人體工學座椅，適合長時間工作'
    },
    community: {
      title: '社群交流',
      description: '與其他工作者交流，建立人脈'
    },
    flexible: {
      title: '彈性方案',
      description: '日租、週租、月租，滿足不同需求'
    }
  },
  types: {
    title: '工作空間類型',
    business: {
      title: '商務中心',
      description: '專業的商務環境，配備完整設施',
      features: ['會議室', '打印服務', '接待服務'],
      price: 'NT$ 400/日',
      bestFor: '商務人士、會議需求'
    },
    seaview: {
      title: '海景工作空間',
      description: '面海的工作環境，享受海風和寧靜',
      features: ['海景視野', '安靜環境', '自然光'],
      price: 'NT$ 250/日',
      bestFor: '創作者、需要靈感'
    },
    mountain: {
      title: '山景工作坊',
      description: '山景環繞的寧靜空間，適合深度思考',
      features: ['山景視野', '清新空氣', '寧靜環境'],
      price: 'NT$ 280/日',
      bestFor: '深度工作者、冥想者'
    },
    cafe: {
      title: '咖啡廳工作空間',
      description: '溫馨的咖啡廳氛圍，提供免費咖啡',
      features: ['免費咖啡', '輕鬆氛圍', '社交機會'],
      price: 'NT$ 150/日',
      bestFor: '自由工作者、社交需求'
    }
  },
  facilities: {
    title: '設施特色',
    list: [
      {
        icon: '📶',
        name: '高速WiFi',
        description: '100Mbps以上網路速度'
      },
      {
        icon: '🪑',
        name: '舒適座椅',
        description: '人體工學設計，長時間工作不累'
      },
      {
        icon: '☕',
        name: '咖啡供應',
        description: '免費咖啡和茶水'
      },
      {
        icon: '🏢',
        name: '會議室',
        description: '配備投影設備的會議空間'
      }
    ]
  },
  process: {
    title: '預約流程',
    steps: [
      {
        title: '選擇工作空間',
        description: '根據工作需求選擇最適合的環境'
      },
      {
        title: '選擇方案',
        description: '日租、週租或月租，選擇最適合的方案'
      },
      {
        title: '確認預約',
        description: '我們會立即確認您的預約並發送確認信'
      },
      {
        title: '開始工作',
        description: '準時到達，享受舒適的工作環境'
      }
    ]
  }
};

// 英文翻譯
const enUS = {
  title: 'Coworking Spaces',
  subtitle: 'From quiet libraries to vibrant startup spaces, from sea view studios to mountain view workshops, we provide diverse work environment options',
  cta: 'Book Workspace',
  startBooking: 'Start Booking Now',
  features: {
    wifi: {
      title: 'High-speed Internet',
      description: 'Stable and fast WiFi to ensure work efficiency'
    },
    comfort: {
      title: 'Comfortable Environment',
      description: 'Ergonomic chairs suitable for long hours of work'
    },
    community: {
      title: 'Community Exchange',
      description: 'Connect with other workers and build networks'
    },
    flexible: {
      title: 'Flexible Plans',
      description: 'Daily, weekly, or monthly rentals to meet different needs'
    }
  },
  types: {
    title: 'Workspace Types',
    business: {
      title: 'Business Center',
      description: 'Professional business environment with complete facilities',
      features: ['Meeting Room', 'Printing Service', 'Reception Service'],
      price: 'NT$ 400/day',
      bestFor: 'Business people, meeting needs'
    },
    seaview: {
      title: 'Sea View Workspace',
      description: 'Sea-facing work environment, enjoy sea breeze and tranquility',
      features: ['Sea View', 'Quiet Environment', 'Natural Light'],
      price: 'NT$ 250/day',
      bestFor: 'Creators, need inspiration'
    },
    mountain: {
      title: 'Mountain View Workshop',
      description: 'Peaceful space surrounded by mountain views, suitable for deep thinking',
      features: ['Mountain View', 'Fresh Air', 'Peaceful Environment'],
      price: 'NT$ 280/day',
      bestFor: 'Deep workers, meditators'
    },
    cafe: {
      title: 'Cafe Workspace',
      description: 'Cozy cafe atmosphere with free coffee',
      features: ['Free Coffee', 'Relaxed Atmosphere', 'Social Opportunities'],
      price: 'NT$ 150/day',
      bestFor: 'Freelancers, social needs'
    }
  },
  facilities: {
    title: 'Facility Features',
    list: [
      {
        icon: '📶',
        name: 'High-speed WiFi',
        description: '100Mbps+ internet speed'
      },
      {
        icon: '🪑',
        name: 'Comfortable Seating',
        description: 'Ergonomic design for long hours of work'
      },
      {
        icon: '☕',
        name: 'Coffee Service',
        description: 'Free coffee and tea'
      },
      {
        icon: '🏢',
        name: 'Meeting Room',
        description: 'Meeting space with projection equipment'
      }
    ]
  },
  process: {
    title: 'Booking Process',
    steps: [
      {
        title: 'Choose Workspace',
        description: 'Select the most suitable environment based on work needs'
      },
      {
        title: 'Select Plan',
        description: 'Daily, weekly, or monthly rental, choose the most suitable plan'
      },
      {
        title: 'Confirm Booking',
        description: 'We will immediately confirm your booking and send confirmation'
      },
      {
        title: 'Start Working',
        description: 'Arrive on time and enjoy a comfortable work environment'
      }
    ]
  }
};

export default WorkspaceIntroPage; 